import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/common/page-header'
import { Button } from '@/components/ui/button'
import { PageLoader } from '@/components/ui/page-loader'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Save, Upload, ImageIcon, Trash2, Eye } from 'lucide-react'
import { companyService } from '@/services/company-service'
import { useAuthStore } from '@/store/auth-store'
import { AddressAutocomplete } from '@/components/shared/address-autocomplete'
import { OpportunityFeedSettings } from '@/components/settings/opportunity-feed-settings'
import { QuickBooksSettings } from '@/components/settings/quickbooks-settings'
import { BillingSettings } from '@/components/settings/billing-settings'

/* ── Save feedback ──
 * A settings save that fails must say so. These handlers used to swallow the
 * error entirely, so a rejected request was indistinguishable from a successful
 * one and a change could appear saved while never reaching the database. */
export function saveErrorMessage(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: { message?: string; error?: string } } })?.response
  const fromServer = res?.data?.message || res?.data?.error
  if (fromServer) return fromServer
  if (res?.status === 401 || res?.status === 403) return "Your session has expired. Sign in again and retry."
  if (res?.status) return `Could not save (server returned ${res.status}). Please try again.`
  // No response at all — request never completed.
  return "Could not save — check your connection and try again."
}

function SaveStatus({ state }: { state: { status: 'error'; message: string } | { status: 'saved' } | null }) {
  if (!state) return null
  if (state.status === 'saved') return <p className="text-sm text-muted-foreground self-center">Saved</p>
  // Same inline treatment as the logo upload error in this page.
  return <p className="text-sm text-destructive self-center">{state.message}</p>
}

/* ── Phone number auto-format (xxx-xxx-xxxx) ── */
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

/* ── Website URL auto-format ── */
function formatWebsite(value: string): string {
  // Don't touch empty values or values already being typed
  return value.trim()
}

function ensureWebsiteProtocol(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`
  return trimmed
}

/* ── US States ── */
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
]

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp']

const TEMPLATES = [
  { id: 'classic', name: 'Professional Classic', desc: 'Brand-color header bar, cover photo, formal pricing tables. Great for municipal clients.' },
  { id: 'modern', name: 'Modern Minimal', desc: 'Clean white space, card-style pricing, accent line. Modern and sophisticated.' },
  { id: 'bold', name: 'Bold Statement', desc: 'Large brand-color hero, bullet scope, investment bar. High-impact presentation.' },
  { id: 'technical', name: 'Technical Detail', desc: 'Data tables, reference numbers, numbered items. Engineering-focused.' },
  { id: 'compact', name: 'Streamlined Compact', desc: 'Quick 2-page format, simple cost summary, signature block. Fast turnaround.' },
  { id: 'monolith', name: 'Monolith Editorial', desc: 'Premium magazine feel. Oversized display type, photo strip, hairline rules. Sophisticated and refined.' },
]

type TabType = 'contact' | 'templates' | 'proposal-content' | 'general' | 'opportunity-feed' | 'integrations' | 'billing'

interface AboutSectionState {
  text: string
  includeInProposal: boolean
}

export function SettingsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialTab = (searchParams.get('tab') as TabType) || 'contact'
  const [activeTab, setActiveTab] = useState<TabType>(initialTab)
  const [selectedTemplate, setSelectedTemplate] = useState('classic')

  // Loading state — don't render content until backend data arrives
  const [settingsLoaded, setSettingsLoaded] = useState(false)

  // Profile Tab
  const [companyName, setCompanyName] = useState('')
  const [companyEmail, setCompanyEmail] = useState('')
  const [companyPhone, setCompanyPhone] = useState('')
  const [companyAddress, setCompanyAddress] = useState('')
  const [companyCity, setCompanyCity] = useState('')
  const [companyState, setCompanyState] = useState('')
  const [companyZip, setCompanyZip] = useState('')
  const [companyWebsite, setCompanyWebsite] = useState('')
  const [requireApproval, setRequireApproval] = useState(true)
  const [invoicingEnabled, setInvoicingEnabled] = useState(false)
  const [invoicingRoles, setInvoicingRoles] = useState<string[]>(['ADMIN', 'MANAGER'])

  // Brand Assets Tab
  const [brandColor, setBrandColor] = useState('') // '' = no brand color set (new tenants start unbranded)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoError, setLogoError] = useState<string | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [isDeletingLogo, setIsDeletingLogo] = useState(false)
  const [savedLogoUrl, setSavedLogoUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Cover photo upload state
  const [isUploadingCover, setIsUploadingCover] = useState(false)
  const [deletingCoverUrl, setDeletingCoverUrl] = useState<string | null>(null) // URL being deleted
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null)
  const coverFileInputRef = useRef<HTMLInputElement>(null)
  const [customCoverPhotos, setCustomCoverPhotos] = useState<string[]>([]) // library of uploaded photos

  // About Company Tab
  const COVER_PHOTOS = [
    { id: '/images/covers/cover-1.jpg', label: 'Surface Work', focus: 'center center' },
    { id: '/images/covers/cover-2.jpg', label: 'Concrete Structure', focus: 'center 70%' },
    { id: '/images/covers/cover-3.jpg', label: 'Night Paving', focus: 'center center' },
    { id: '/images/covers/cover-4.jpg', label: 'Sweeper', focus: 'center 40%' },
    { id: 'https://images.unsplash.com/photo-1590674899484-d5640e854abe?w=1600&h=900&fit=crop&q=80', label: 'Aerial Roadway', focus: 'center center' },
    { id: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=900&fit=crop&q=80', label: 'Crew at Work', focus: 'center 60%' },
    { id: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&h=900&fit=crop&q=80', label: 'Construction Site', focus: 'center center' },
    { id: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=1600&h=900&fit=crop&q=80', label: 'Surface Close-Up', focus: 'center center' },
    { id: 'https://images.unsplash.com/photo-1720166671019-744dcef65d05?w=1600&h=900&fit=crop&q=80', label: 'Project Overview', focus: 'center center' },
    { id: 'https://images.unsplash.com/photo-1515162816999-a0c47dc192f7?w=1600&h=900&fit=crop&q=80', label: 'Mountain Highway', focus: 'center center' },
  ]

  const ABOUT_MENU = [
    { key: 'coverPhoto', label: 'Cover Photo', placeholder: '' },
    { key: 'coverLetter', label: 'Cover Letter', placeholder: 'Dear [Client],\n\nThank you for the opportunity to provide this proposal for your paving project...' },
    { key: 'aboutCompany', label: 'Our Company', placeholder: 'Tell your company story...' },
    { key: 'mission', label: 'Our Mission', placeholder: 'What drives your company...' },
    { key: 'whoWeServe', label: 'Who We Serve', placeholder: 'Describe your target clients...' },
    { key: 'reasonsToChoose', label: 'Why Us?', placeholder: 'Why clients should choose you...' },
    { key: 'termsConditions', label: 'Terms and Conditions', placeholder: 'Your company terms...' },
  ] as const
  const [activeAboutKey, setActiveAboutKey] = useState('coverLetter')
  const [aboutSections, setAboutSections] = useState<Record<string, AboutSectionState>>({
    aboutCompany: { text: '', includeInProposal: false },
    mission: { text: '', includeInProposal: false },
    whoWeServe: { text: '', includeInProposal: false },
    reasonsToChoose: { text: '', includeInProposal: false },
    termsConditions: { text: '', includeInProposal: false },
  })

  // Proposal Content Tab
  const [coverLetter, setCoverLetter] = useState('')
  const [includeCoverLetter, setIncludeCoverLetter] = useState(false)
  const [coverPhotoUrl, setCoverPhotoUrl] = useState('/images/covers/cover-1.jpg')
  const [headerTintEnabled, setHeaderTintEnabled] = useState(true)

  // Load existing settings from backend on mount
  useEffect(() => {
    companyService.getSettings().then((settings) => {
      if (settings.companyName) setCompanyName(settings.companyName)
      if (settings.companyEmail) setCompanyEmail(settings.companyEmail)
      if (settings.companyPhone) setCompanyPhone(settings.companyPhone)
      if (settings.companyAddress) setCompanyAddress(settings.companyAddress)
      if (settings.companyCity) setCompanyCity(settings.companyCity)
      if (settings.companyState) setCompanyState(settings.companyState)
      if (settings.companyZip) setCompanyZip(settings.companyZip)
      if (settings.companyWebsite) setCompanyWebsite(settings.companyWebsite)
      setBrandColor(settings.primaryColor || '') // empty when the tenant hasn't picked a brand color yet
      if (settings.proposalTemplate) setSelectedTemplate(settings.proposalTemplate)
      if (settings.logoUrl) setSavedLogoUrl(settings.logoUrl)
      if (settings.aboutUs) {
        try {
          const parsed = JSON.parse(settings.aboutUs)
          setAboutSections(prev => ({ ...prev, ...parsed }))
        } catch {
          // aboutUs is plain text, not JSON — put it in aboutCompany
          setAboutSections(prev => ({ ...prev, aboutCompany: { text: settings.aboutUs || '', includeInProposal: true } }))
        }
      }
      if (settings.coverLetter) setCoverLetter(settings.coverLetter)
      if (settings.includeCoverLetter !== undefined) setIncludeCoverLetter(settings.includeCoverLetter)
      if (settings.coverPhotoUrl) setCoverPhotoUrl(settings.coverPhotoUrl)
      if (settings.headerTintEnabled !== undefined) setHeaderTintEnabled(settings.headerTintEnabled)
      try {
        const lib = typeof (settings as any).customCoverPhotos === 'string'
          ? JSON.parse((settings as any).customCoverPhotos)
          : ((settings as any).customCoverPhotos ?? [])
        if (Array.isArray(lib)) setCustomCoverPhotos(lib)
      } catch { /* keep empty */ }
      if (settings.requireApproval !== undefined) setRequireApproval(settings.requireApproval)
      if (settings.invoicingEnabled !== undefined) setInvoicingEnabled(settings.invoicingEnabled)
      if (settings.invoicingRoles) {
        try {
          const roles = typeof settings.invoicingRoles === 'string' ? JSON.parse(settings.invoicingRoles) : settings.invoicingRoles
          if (Array.isArray(roles)) setInvoicingRoles(roles)
        } catch { /* keep default */ }
      }
    }).catch(() => {
      // Settings not found or not authenticated — keep defaults
    }).finally(() => {
      setSettingsLoaded(true)
    })

  }, [])

  // Logo handlers — selecting a file auto-saves immediately
  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogoError(null)
    const file = e.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setLogoError('Please upload a PNG, JPEG, SVG, or WebP image.')
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      setLogoError('Image must be under 5MB.')
      return
    }

    const reader = new FileReader()
    reader.onload = async () => {
      const dataUrl = reader.result as string

      // Validate dimensions: must be square (or near-square) and at least 200×200
      const img = new Image()
      img.onload = async () => {
        const { width, height } = img
        const MIN_SIZE = 200
        const aspectRatio = width / height

        if (width < MIN_SIZE || height < MIN_SIZE) {
          setLogoError(`Logo must be at least ${MIN_SIZE}×${MIN_SIZE}px. Yours is ${width}×${height}px.`)
          if (fileInputRef.current) fileInputRef.current.value = ''
          return
        }

        // Allow ±20% aspect ratio tolerance (0.8 to 1.2)
        if (aspectRatio < 0.8 || aspectRatio > 1.2) {
          setLogoError(`Logo should be square. Yours is ${width}×${height}px — it will be cropped to fit.`)
          // Warning only — still proceed with upload
        }

        setLogoPreview(dataUrl)
        setIsUploadingLogo(true)
        try {
          await companyService.updateLogo(dataUrl)
          setSavedLogoUrl(dataUrl)
          setLogoPreview(null)
        } catch (err: any) {
          const message = err?.response?.data?.message || 'Failed to save logo. Please try again.'
          setLogoError(message)
        } finally {
          setIsUploadingLogo(false)
          if (fileInputRef.current) fileInputRef.current.value = ''
        }
      }
      img.onerror = () => {
        setLogoError('Could not read image dimensions. Please try another file.')
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
      img.src = dataUrl
    }
    reader.onerror = () => {
      setLogoError('Failed to read image file. Please try again.')
    }
    reader.readAsDataURL(file)
  }

  const handleDeleteLogo = async () => {
    setIsDeletingLogo(true)
    setLogoError(null)

    try {
      await companyService.deleteLogo()
      setSavedLogoUrl(null)
      setLogoPreview(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to delete logo. Please try again.'
      setLogoError(message)
    } finally {
      setIsDeletingLogo(false)
    }
  }

  const handleCoverUpload = (file: File) => {
    if (file.size > 20 * 1024 * 1024) {
      setCoverUploadError('Image must be under 20MB.')
      return
    }
    setCoverUploadError(null)
    setIsUploadingCover(true)

    // Step 1: read file into a data URL
    const reader = new FileReader()
    reader.onerror = () => {
      setCoverUploadError('Failed to read file. Please try again.')
      setIsUploadingCover(false)
    }
    reader.onload = () => {
      const rawDataUrl = reader.result as string

      // Step 2: draw onto canvas to compress (max 1920px, JPEG q0.82)
      // This keeps the payload well under Vercel's 4.5 MB body limit.
      const img = new Image()
      img.onerror = () => {
        setCoverUploadError('Could not load image. Please try a different file.')
        setIsUploadingCover(false)
      }
      img.onload = () => {
        try {
          const MAX_W = 1920
          const scale = img.width > MAX_W ? MAX_W / img.width : 1
          const canvas = document.createElement('canvas')
          canvas.width = Math.round(img.width * scale)
          canvas.height = Math.round(img.height * scale)
          const ctx = canvas.getContext('2d')
          if (!ctx) throw new Error('Canvas context unavailable')
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          const compressed = canvas.toDataURL('image/jpeg', 0.82)

          // Step 3: upload to backend (adds to library + auto-selects)
          companyService.addCoverPhoto(compressed)
            .then((branding) => {
              // Parse the updated library from the response
              try {
                const lib = typeof (branding as any).customCoverPhotos === 'string'
                  ? JSON.parse((branding as any).customCoverPhotos)
                  : ((branding as any).customCoverPhotos ?? [])
                if (Array.isArray(lib)) setCustomCoverPhotos(lib)
              } catch { /* keep existing */ }
              // Append cache-buster so the browser fetches the new file
              const raw = branding.coverPhotoUrl ?? '/images/covers/cover-1.jpg'
              const busted = raw.startsWith('http') ? `${raw}?t=${Date.now()}` : raw
              setCoverPhotoUrl(busted)
            })
            .catch((err: any) => {
              const message = err?.response?.data?.message || 'Upload failed. Please try again.'
              setCoverUploadError(message)
            })
            .finally(() => {
              setIsUploadingCover(false)
            })
        } catch (err: any) {
          setCoverUploadError(err?.message || 'Failed to process image.')
          setIsUploadingCover(false)
        }
      }
      img.src = rawDataUrl
    }
    reader.readAsDataURL(file)
  }

  const handleAboutSectionChange = (section: string, text: string) => {
    setAboutSections(prev => ({
      ...prev,
      [section]: { ...prev[section], text }
    }))
  }

  const handleAboutSectionToggle = (section: string) => {
    setAboutSections(prev => ({
      ...prev,
      [section]: { ...prev[section], includeInProposal: !prev[section].includeInProposal }
    }))
  }

  // Save feedback. These saves used to swallow every failure (`catch { /* TODO:
  // show error toast */ }`), so a rejected request looked identical to a successful
  // one — the button simply returned to "Save Changes". A brand-colour change was
  // reported as "saved" while the value never reached the database. Surface both
  // outcomes, using the same inline `text-destructive` treatment as logoError above.
  type SaveState = { status: 'error'; message: string } | { status: 'saved' } | null

  const [contactSave, setContactSave] = useState<SaveState>(null)
  const [isSavingContact, setIsSavingContact] = useState(false)
  const handleSaveContact = async () => {
    setIsSavingContact(true)
    setContactSave(null)
    try {
      await companyService.updateSettings({
        companyName,
        companyEmail,
        companyPhone,
        companyAddress,
        companyCity,
        companyState,
        companyZip,
        companyWebsite: ensureWebsiteProtocol(companyWebsite),
        primaryColor: brandColor,
      })
      setContactSave({ status: 'saved' })
    } catch (err) {
      setContactSave({ status: 'error', message: saveErrorMessage(err) })
    } finally {
      setIsSavingContact(false)
    }
  }

  const [aboutSave, setAboutSave] = useState<SaveState>(null)
  const [isSavingAbout, setIsSavingAbout] = useState(false)
  const handleSaveAbout = async () => {
    setIsSavingAbout(true)
    setAboutSave(null)
    try {
      await companyService.updateSettings({
        aboutUs: JSON.stringify(aboutSections),
        coverLetter,
        includeCoverLetter,
        coverPhotoUrl,
        headerTintEnabled,
      })
      setAboutSave({ status: 'saved' })
    } catch (err) {
      setAboutSave({ status: 'error', message: saveErrorMessage(err) })
    } finally {
      setIsSavingAbout(false)
    }
  }

  // Shared tiny elements used across thumbnails
  const Swatch = ({ color, w = '100%', h = '4px', r = '1px', mb = '0' }: { color: string; w?: string; h?: string; r?: string; mb?: string }) => (
    <div style={{ width: w, height: h, borderRadius: r, backgroundColor: color, marginBottom: mb, flexShrink: 0 }} />
  )
  const TextLine = ({ w = '100%', h = '3px', mb = '2px' }: { w?: string; h?: string; mb?: string }) => (
    <div style={{ width: w, height: h, borderRadius: '1px', backgroundColor: '#d1d5db', marginBottom: mb }} />
  )
  const PhotoBlock = ({ h = '18px', mb = '4px' }: { h?: string; mb?: string }) => (
    <div style={{ width: '100%', height: h, borderRadius: '1px', background: 'linear-gradient(135deg, #c7d2db 0%, #a8b5c2 50%, #c7d2db 100%)', marginBottom: mb, flexShrink: 0 }} />
  )
  const TableRow = ({ idx, bc }: { idx: number; bc: string }) => (
    <div style={{ display: 'flex', gap: '2px', padding: '1.5px 0', backgroundColor: idx % 2 === 0 ? '#f3f4f6' : '#fff' }}>
      <div style={{ flex: 3, height: '2.5px', borderRadius: '0.5px', backgroundColor: '#d1d5db' }} />
      <div style={{ flex: 1, height: '2.5px', borderRadius: '0.5px', backgroundColor: '#d1d5db' }} />
      <div style={{ flex: 1, height: '2.5px', borderRadius: '0.5px', backgroundColor: idx === 0 ? bc : '#d1d5db' }} />
    </div>
  )

  const MiniPage = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <div style={{ width: '90px', height: '120px', background: '#fff', borderRadius: '2px', boxShadow: '0 1px 4px rgba(0,0,0,0.12)', overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column', ...style }}>
      {children}
    </div>
  )

  const logoUrl = savedLogoUrl || logoPreview
  const MiniLogo = ({ size = 12, style }: { size?: number; style?: React.CSSProperties }) => (
    logoUrl
      ? <div style={{ width: size, height: size, borderRadius: '1px', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', ...style }}>
          <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
      : <div style={{ width: size, height: size, borderRadius: '1px', backgroundColor: '#e5e7eb', flexShrink: 0, ...style }} />
  )

  const renderTemplateThumbnail = (templateId: string) => {
    const bc = brandColor || '#3B82F6' // preview fallback when no brand color is set
    switch (templateId) {
      case 'classic':
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Cover page */}
            <MiniPage>
              <div style={{ backgroundColor: bc, padding: '5px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <MiniLogo size={10} style={{ borderRadius: '1px' }} />
                  <div><TextLine w="20px" h="1.5px" mb="1px" /><TextLine w="16px" h="1.5px" mb="0" /></div>
                </div>
                <div style={{ width: '50%', height: '1.5px', backgroundColor: 'rgba(255,255,255,0.4)', marginBottom: '1px', borderRadius: '0.5px' }} />
                <div style={{ width: '80%', height: '3px', backgroundColor: '#fff', borderRadius: '0.5px' }} />
              </div>
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <PhotoBlock h="26px" mb="4px" />
                <TextLine w="75%" h="3.5px" mb="4px" />
                <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                  <div style={{ flex: 1 }}><TextLine w="40%" h="1.5px" mb="1px" /><TextLine w="70%" h="2.5px" mb="0" /></div>
                  <div style={{ flex: 1 }}><TextLine w="40%" h="1.5px" mb="1px" /><TextLine w="60%" h="2px" mb="0" /></div>
                </div>
                <div style={{ marginTop: 'auto', borderTop: `1px solid ${bc}30`, paddingTop: '3px', display: 'flex', justifyContent: 'space-between' }}>
                  <TextLine w="25%" h="1.5px" mb="0" />
                  <TextLine w="20%" h="1.5px" mb="0" />
                </div>
              </div>
            </MiniPage>
            {/* Body page */}
            <MiniPage>
              <Swatch color={bc} h="2px" r="0" mb="0" />
              <div style={{ padding: '3px 5px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '3px' }}>
                <MiniLogo size={7} style={{ borderRadius: '1px' }} />
                <div><TextLine w="30px" h="2px" mb="0" /></div>
              </div>
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '3px' }}>
                  <Swatch color={bc} w="2px" h="6px" r="0.5px" mb="0" />
                  <TextLine w="40%" h="2px" mb="0" />
                </div>
                <TextLine w="95%" h="1.5px" mb="1px" />
                <TextLine w="80%" h="1.5px" mb="4px" />
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '2px' }}>
                  <Swatch color={bc} w="2px" h="6px" r="0.5px" mb="0" />
                  <TextLine w="45%" h="2px" mb="0" />
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '1px', overflow: 'hidden', marginBottom: '4px' }}>
                  <Swatch color={bc} h="4px" r="0" mb="0" />
                  {[0,1,2,3].map(i => <TableRow key={i} idx={i} bc={bc} />)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                  <Swatch color={bc} w="40%" h="3px" r="1px" mb="0" />
                </div>
                <div style={{ marginTop: 'auto' }}>
                  <Swatch color={bc} h="4px" r="0" mb="0" />
                </div>
              </div>
            </MiniPage>
          </div>
        )
      case 'modern':
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <MiniPage>
              <div style={{ padding: '6px', backgroundColor: '#fafaf8' }}>
                <MiniLogo size={12} style={{ marginBottom: '6px' }} />
                <TextLine w="80%" h="5px" mb="3px" />
                <TextLine w="55%" h="3px" mb="3px" />
                <Swatch color={bc} w="24px" h="2px" mb="0" />
              </div>
              <Swatch color={bc} h="1px" r="0" mb="0" />
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <PhotoBlock h="26px" mb="5px" />
                <TextLine w="25%" h="2px" mb="1px" />
                <TextLine w="55%" h="3px" mb="4px" />
                <TextLine w="25%" h="2px" mb="1px" />
                <TextLine w="50%" h="3px" mb="4px" />
                <TextLine w="25%" h="2px" mb="1px" />
                <TextLine w="45%" h="2px" mb="0" />
              </div>
            </MiniPage>
            <MiniPage>
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Swatch color={bc} w="40%" h="2px" r="0" mb="3px" />
                <TextLine w="95%" h="2px" mb="1px" />
                <TextLine w="85%" h="2px" mb="4px" />
                <Swatch color={bc} w="45%" h="2px" r="0" mb="3px" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', marginBottom: '4px' }}>
                  {[0,1,2,3].map(i => (
                    <div key={i} style={{ padding: '3px', backgroundColor: '#fafaf8', borderRadius: '1px' }}>
                      <TextLine w="80%" h="2px" mb="1px" />
                      <TextLine w="50%" h="2px" mb="0" />
                    </div>
                  ))}
                </div>
                <div style={{ padding: '4px', backgroundColor: '#fafaf8', borderLeft: `2px solid ${bc}`, borderRadius: '1px', marginBottom: '4px' }}>
                  <TextLine w="45%" h="2px" mb="1px" />
                  <Swatch color={bc} w="50%" h="4px" r="0.5px" mb="0" />
                </div>
                <div style={{ marginTop: 'auto', textAlign: 'center' }}>
                  <Swatch color={bc} w="50%" h="2px" r="0.5px" mb="0" />
                </div>
              </div>
            </MiniPage>
          </div>
        )
      case 'bold':
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <MiniPage style={{ flexDirection: 'row' }}>
              <div style={{ flex: '0 0 40%', backgroundColor: bc, padding: '5px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <MiniLogo size={14} style={{ marginBottom: '4px', borderRadius: '1px' }} />
                  <div style={{ width: '80%', height: '3px', backgroundColor: '#fff', marginBottom: '2px', borderRadius: '0.5px' }} />
                  <div style={{ width: '60%', height: '3px', backgroundColor: '#fff', borderRadius: '0.5px' }} />
                </div>
                <div>
                  <div style={{ width: '70%', height: '2px', backgroundColor: 'rgba(255,255,255,0.4)', borderRadius: '0.5px' }} />
                </div>
              </div>
              <div style={{ flex: '0 0 60%', padding: '5px', display: 'flex', flexDirection: 'column' }}>
                <PhotoBlock h="28px" mb="4px" />
                <TextLine w="25%" h="2px" mb="1px" />
                <TextLine w="65%" h="3px" mb="3px" />
                <TextLine w="25%" h="2px" mb="1px" />
                <TextLine w="55%" h="3px" mb="3px" />
                <TextLine w="25%" h="2px" mb="1px" />
                <TextLine w="45%" h="2px" mb="0" />
                <div style={{ marginTop: 'auto' }}><TextLine w="30%" h="2px" mb="0" /></div>
              </div>
            </MiniPage>
            <MiniPage style={{ borderLeft: `3px solid ${bc}` }}>
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Swatch color={bc} w="40%" h="2px" r="0" mb="3px" />
                <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-start', marginBottom: '2px' }}>
                  <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: bc, flexShrink: 0, marginTop: '0px' }} />
                  <TextLine w="90%" h="2px" mb="0" />
                </div>
                <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: bc, flexShrink: 0 }} />
                  <TextLine w="75%" h="2px" mb="0" />
                </div>
                <Swatch color={bc} w="35%" h="2px" r="0" mb="2px" />
                {[0,1,2,3].map(i => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '1.5px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <TextLine w="55%" h="2px" mb="0" />
                    <TextLine w="20%" h="2px" mb="0" />
                  </div>
                ))}
                <Swatch color={bc} h="6px" r="1px" mb="0" />
                <div style={{ marginTop: 'auto', borderTop: `2px solid ${bc}`, paddingTop: '3px', textAlign: 'center' }}>
                  <Swatch color={bc} w="60%" h="2px" r="0.5px" mb="0" />
                </div>
              </div>
            </MiniPage>
          </div>
        )
      case 'technical':
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <MiniPage>
              <div style={{ backgroundColor: '#f3f4f6', borderBottom: `2px solid ${bc}`, padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: '5px', color: bc, fontWeight: 'bold', marginBottom: '2px' }}>REF#NPG-0847</div>
                  <TextLine w="55%" h="4px" mb="0" />
                </div>
                <MiniLogo size={14} />
              </div>
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {[0,1,2,3].map(i => (
                  <div key={i} style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '2px 0', backgroundColor: i % 2 === 0 ? '#f9fafb' : '#fff' }}>
                    <TextLine w="35%" h="2px" mb="0" />
                    <div style={{ width: '4px' }} />
                    <TextLine w="55%" h="2px" mb="0" />
                  </div>
                ))}
                <div style={{ marginTop: '4px' }}><PhotoBlock h="24px" mb="4px" /></div>
                <Swatch color={bc} w="30%" h="2px" r="0" mb="2px" />
                <TextLine w="70%" h="3px" mb="1px" />
                <TextLine w="50%" h="2px" mb="0" />
              </div>
            </MiniPage>
            <MiniPage>
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ borderBottom: `2px solid ${bc}`, paddingBottom: '2px', marginBottom: '3px' }}>
                  <Swatch color={bc} w="40%" h="2px" r="0" mb="0" />
                </div>
                <TextLine w="95%" h="2px" mb="1px" />
                <TextLine w="80%" h="2px" mb="4px" />
                <div style={{ borderBottom: `2px solid ${bc}`, paddingBottom: '2px', marginBottom: '2px' }}>
                  <Swatch color={bc} w="45%" h="2px" r="0" mb="0" />
                </div>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '1px', overflow: 'hidden', marginBottom: '4px' }}>
                  <div style={{ display: 'flex', gap: '1px', padding: '2px', backgroundColor: '#f3f4f6', borderBottom: '1px solid #d1d5db' }}>
                    <TextLine w="10%" h="2px" mb="0" /><TextLine w="40%" h="2px" mb="0" /><TextLine w="15%" h="2px" mb="0" /><TextLine w="15%" h="2px" mb="0" /><TextLine w="20%" h="2px" mb="0" />
                  </div>
                  {[0,1,2,3].map(i => <TableRow key={i} idx={i} bc={bc} />)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ width: '45%' }}>
                    <TextLine w="100%" h="2px" mb="1px" />
                    <TextLine w="100%" h="2px" mb="1px" />
                    <Swatch color={bc} h="3px" r="0.5px" mb="0" />
                  </div>
                </div>
                <div style={{ marginTop: 'auto', borderBottom: `2px solid ${bc}`, paddingBottom: '2px' }}>
                  <Swatch color={bc} w="50%" h="2px" r="0" mb="1px" />
                </div>
                <TextLine w="90%" h="2px" mb="1px" />
                <TextLine w="85%" h="2px" mb="0" />
              </div>
            </MiniPage>
          </div>
        )
      case 'compact':
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
            {/* Compact is a single page */}
            <MiniPage style={{ width: '110px', height: '140px' }}>
              <div style={{ backgroundColor: bc, padding: '4px 5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ width: '40px', height: '3px', backgroundColor: '#fff', borderRadius: '0.5px', marginBottom: '1px' }} />
                  <div style={{ width: '30px', height: '2px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '0.5px' }} />
                </div>
                <MiniLogo size={12} style={{ borderRadius: '1px' }} />
              </div>
              <div style={{ padding: '4px 5px', flex: 1, display: 'flex', flexDirection: 'column', fontSize: '4px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '4px', color: bc, fontWeight: 'bold', marginBottom: '3px' }}>Proposal #</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', marginBottom: '3px' }}>
                  <div><TextLine w="35%" h="2px" mb="1px" /><TextLine w="60%" h="2.5px" mb="0" /></div>
                  <div><TextLine w="30%" h="2px" mb="1px" /><TextLine w="50%" h="2.5px" mb="0" /></div>
                </div>
                <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '3px', marginBottom: '3px' }}>
                  <TextLine w="25%" h="2px" mb="1px" />
                  <TextLine w="55%" h="2.5px" mb="0" />
                </div>
                <TextLine w="95%" h="2px" mb="1px" />
                <TextLine w="80%" h="2px" mb="3px" />
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '1px', overflow: 'hidden', marginBottom: '3px' }}>
                  {[0,1,2].map(i => <TableRow key={i} idx={i} bc={bc} />)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '3px' }}>
                  <Swatch color={bc} w="35%" h="4px" r="1px" mb="0" />
                </div>
                <div style={{ marginTop: 'auto', display: 'flex', gap: '6px' }}>
                  <div style={{ flex: 1, borderTop: '1px solid #ccc', paddingTop: '2px' }}><TextLine w="55%" h="2px" mb="0" /></div>
                  <div style={{ flex: 1, borderTop: '1px solid #ccc', paddingTop: '2px' }}><TextLine w="55%" h="2px" mb="0" /></div>
                </div>
              </div>
            </MiniPage>
          </div>
        )
      case 'monolith':
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Cover page — two-column: content left, photo strip right */}
            <MiniPage style={{ flexDirection: 'row', backgroundColor: '#FAF8F4' }}>
              <div style={{ flex: 1, padding: '5px', display: 'flex', flexDirection: 'column', backgroundColor: '#FAF8F4' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '3px', borderBottom: `0.5px solid #E4E2DD`, marginBottom: '6px' }}>
                  <MiniLogo size={9} style={{ borderRadius: '1px' }} />
                  <div><TextLine w="16px" h="1.5px" mb="1px" /><TextLine w="12px" h="1px" mb="0" /></div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: '4px' }}>
                  <div style={{ width: '12px', height: '0.5px', backgroundColor: bc, marginBottom: '4px' }} />
                  <TextLine w="85%" h="5px" mb="2px" />
                  <TextLine w="70%" h="5px" mb="4px" />
                  <div style={{ width: '10px', height: '1.5px', backgroundColor: bc, marginBottom: '4px' }} />
                  <TextLine w="90%" h="1px" mb="1px" />
                  <TextLine w="75%" h="1px" mb="0" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', paddingTop: '3px', borderTop: `0.5px solid #E4E2DD` }}>
                  <div><TextLine w="40%" h="1px" mb="1px" /><TextLine w="70%" h="2px" mb="1px" /><TextLine w="55%" h="1px" mb="0" /></div>
                  <div><TextLine w="40%" h="1px" mb="1px" /><TextLine w="70%" h="2px" mb="1px" /><TextLine w="55%" h="1px" mb="0" /></div>
                </div>
              </div>
              {/* Right photo strip */}
              <div style={{ width: '28px', backgroundColor: bc, position: 'relative', flexShrink: 0 }}>
                <div style={{ position: 'absolute', bottom: '5px', left: '3px', right: '3px' }}>
                  <div style={{ width: '100%', height: '0.5px', backgroundColor: 'rgba(255,255,255,0.5)', marginBottom: '2px' }} />
                  <TextLine w="80%" h="1px" mb="0" />
                </div>
              </div>
            </MiniPage>
            {/* Body page — cream, hairline rules, numbered items */}
            <MiniPage style={{ backgroundColor: '#FAF8F4' }}>
              <div style={{ padding: '5px', flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#FAF8F4' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px', marginBottom: '2px' }}>
                  <span style={{ fontSize: '4px', color: bc, fontWeight: 700 }}>I.</span>
                  <TextLine w="50%" h="1.5px" mb="0" />
                </div>
                <TextLine w="75%" h="3px" mb="2px" />
                <div style={{ height: '0.5px', backgroundColor: '#E4E2DD', marginBottom: '4px' }} />
                <TextLine w="95%" h="1px" mb="1px" />
                <TextLine w="80%" h="1px" mb="4px" />
                {[0,1].map(i => (
                  <div key={i} style={{ paddingBottom: '3px', marginBottom: '3px', borderBottom: `0.5px solid #E4E2DD` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                        <span style={{ fontSize: '3px', color: bc }}>{'●'}</span>
                        <TextLine w="30px" h="2px" mb="0" />
                      </div>
                      <TextLine w="14px" h="2px" mb="0" />
                    </div>
                    <div style={{ paddingLeft: '4px' }}><TextLine w="85%" h="1px" mb="1px" /><TextLine w="70%" h="1px" mb="0" /></div>
                  </div>
                ))}
                <div style={{ marginTop: 'auto', padding: '3px', backgroundColor: '#fff', border: `0.5px solid #E4E2DD`, borderRadius: '1px' }}>
                  <TextLine w="50%" h="1px" mb="1px" />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Swatch color={bc} w="35%" h="3px" r="0.5px" mb="0" />
                  </div>
                </div>
              </div>
            </MiniPage>
          </div>
        )
      default:
        return <div style={{ height: '100%', backgroundColor: '#f3f4f6' }}></div>
    }
  }

  if (!settingsLoaded) {
    return (
      <div className="space-y-6">
        <PageHeader title="Company Settings" />
        <Card>
          <CardContent className="settings-panel">
            <PageLoader />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Company Settings" />

      {/* Tabs */}
      <Card>
        <div className="settings-tabs">
          {[
            { id: 'contact' as const, label: 'Company Info' },
            { id: 'templates' as const, label: 'Proposal Templates' },
            { id: 'proposal-content' as const, label: 'Proposal Content' },
            { id: 'opportunity-feed' as const, label: 'Opportunity Feed' },
            { id: 'integrations' as const, label: 'Integrations' },
            { id: 'billing' as const, label: 'Billing' },
            { id: 'general' as const, label: 'General Settings' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <CardContent className="settings-panel">
          {/* Contact Info Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Company Information</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name</Label>
                    <Input id="companyName" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyEmail">Email</Label>
                    <Input id="companyEmail" type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} placeholder="info@company.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyPhone">Phone</Label>
                    <Input id="companyPhone" value={companyPhone} onChange={(e) => setCompanyPhone(formatPhone(e.target.value))} placeholder="555-555-5555" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyWebsite">Website</Label>
                    <Input id="companyWebsite" value={companyWebsite} onChange={(e) => setCompanyWebsite(formatWebsite(e.target.value))} placeholder="www.yourcompany.com" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 mt-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyAddress">Address</Label>
                      <AddressAutocomplete
                        value={companyAddress}
                        onChange={setCompanyAddress}
                        onSelect={(address, city, state, zip) => {
                          setCompanyAddress(address)
                          setCompanyCity(city)
                          setCompanyState(state)
                          setCompanyZip(zip)
                        }}
                        placeholder="Start typing an address..."
                        className="settings-input"
                      />
                    </div>
                    <div className="grid gap-4 grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="companyCity">City</Label>
                        <Input id="companyCity" value={companyCity} onChange={(e) => setCompanyCity(e.target.value)} placeholder="City" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companyState">State</Label>
                        <select id="companyState" className="settings-select" value={companyState} onChange={(e) => setCompanyState(e.target.value)}>
                          <option value="">State</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companyZip">Zip</Label>
                        <Input id="companyZip" value={companyZip} onChange={(e) => setCompanyZip(e.target.value)} placeholder="Zip" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Brand Assets */}
              <div className="pt-6">
                <h3 className="text-lg font-semibold mb-4">Brand Assets</h3>

                {/* Logo Upload */}
                <div className="pb-6 border-b">
                  <Label className="text-base font-medium mb-4 block">Company Logo</Label>
                  <div className="flex items-center gap-4">
                    {(isUploadingLogo ? logoPreview : savedLogoUrl) ? (
                      <div className="relative flex h-20 w-20 items-center justify-center rounded-lg border bg-white p-1">
                        <img
                          src={(isUploadingLogo ? logoPreview : savedLogoUrl) || ''}
                          alt="Logo preview"
                          className="h-full w-full object-contain rounded"
                          style={isUploadingLogo ? { opacity: 0.5 } : undefined}
                        />
                        {isUploadingLogo && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-xs font-medium text-muted-foreground">Saving...</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted">
                        <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={handleLogoSelect}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingLogo}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {savedLogoUrl ? 'Replace Logo' : 'Upload Logo'}
                      </Button>
                      {savedLogoUrl && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleDeleteLogo}
                          disabled={isDeletingLogo || isUploadingLogo}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {isDeletingLogo ? 'Deleting...' : 'Delete Logo'}
                        </Button>
                      )}
                    </div>
                  </div>
                  {logoError && (
                    <p className="mt-2 text-sm text-destructive">{logoError}</p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    Square image recommended (min 200×200px). PNG, JPEG, SVG, or WebP. Max 5MB.
                  </p>
                </div>

                {/* Brand Color */}
                <div className="pt-6">
                  <Label htmlFor="brandColor" className="text-base font-medium mb-4 block">Brand Color</Label>
                  <div className="flex gap-2 items-center">
                    {brandColor ? (
                      <>
                        <input
                          type="color"
                          value={brandColor}
                          onChange={(e) => setBrandColor(e.target.value)}
                          className="h-10 w-12 cursor-pointer rounded border"
                        />
                        <Input
                          id="brandColor"
                          value={brandColor}
                          onChange={(e) => setBrandColor(e.target.value)}
                          className="w-24"
                        />
                        <Button variant="ghost" size="sm" onClick={() => setBrandColor('')}>
                          Clear
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="h-10 w-12 rounded border border-dashed flex items-center justify-center text-[10px] text-muted-foreground">
                          None
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setBrandColor('#3B82F6')}>
                          Set brand color
                        </Button>
                        <span className="text-xs text-muted-foreground">No brand color set</span>
                      </>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Choose a color that represents your brand. This color will be used throughout your proposals.
                  </p>
                </div>
              </div>

              <div className="flex justify-end items-center gap-3">
                <SaveStatus state={contactSave} />
                <Button onClick={handleSaveContact} disabled={isSavingContact}>
                  <Save className="mr-2 h-4 w-4" />
                  {isSavingContact ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}

          {/* Proposal Content Tab */}
          {activeTab === 'proposal-content' && (() => {
            const activeItem = ABOUT_MENU.find(m => m.key === activeAboutKey) || ABOUT_MENU[0]
            const isCoverPhoto = activeAboutKey === 'coverPhoto'
            const isCoverLetter = activeAboutKey === 'coverLetter'
            return (
              <div className="abt-layout">
                {/* Left vertical menu */}
                <nav className="abt-nav">
                  {ABOUT_MENU.map(item => (
                    <button
                      key={item.key}
                      className={`abt-nav-item ${activeAboutKey === item.key ? 'active' : ''}`}
                      onClick={() => setActiveAboutKey(item.key)}
                    >
                      {item.label}
                      {item.key === 'coverPhoto' ? (
                        coverPhotoUrl && (
                          <span className="abt-nav-badge">
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                          </span>
                        )
                      ) : item.key === 'coverLetter' ? (
                        includeCoverLetter && (
                          <span className="abt-nav-badge">
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                          </span>
                        )
                      ) : (
                        aboutSections[item.key]?.includeInProposal && (
                          <span className="abt-nav-badge">
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                          </span>
                        )
                      )}
                    </button>
                  ))}
                </nav>

                {/* Right content panel */}
                <div className="abt-content">
                  {isCoverPhoto ? (
                    <>
                      <div className="abt-content-header">
                        <h3 className="abt-content-title">Cover Photo</h3>
                        <label className="abt-toggle">
                          <input
                            type="checkbox"
                            checked={headerTintEnabled}
                            onChange={async (e) => {
                              const v = e.target.checked
                              setHeaderTintEnabled(v)
                              setAboutSave(null)
                              try {
                                await companyService.updateSettings({ headerTintEnabled: v })
                              } catch (err) {
                                // This one auto-saves on toggle, so on failure put the switch
                                // back where it was — leaving it flipped would show a state the
                                // server never accepted.
                                setHeaderTintEnabled(!v)
                                setAboutSave({ status: 'error', message: saveErrorMessage(err) })
                              }
                            }}
                          />
                          <span>Brand color overlay</span>
                        </label>
                      </div>
                      <p className="abt-helper">Select a cover image for your proposal, or upload your own. When “Brand color overlay” is on, your brand color tints every header photo; turn it off to show photos untinted.</p>

                      {/* Hidden file input */}
                      <input
                        ref={coverFileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          e.target.value = '' // reset so same file can be re-selected
                          if (file) handleCoverUpload(file)
                        }}
                      />

                      {/* Upload button row */}
                      <div className="cp-upload-row" style={{ marginBottom: 4 }}>
                        <button
                          className="cp-upload-btn"
                          onClick={() => coverFileInputRef.current?.click()}
                          disabled={isUploadingCover || !!deletingCoverUrl}
                        >
                          <Upload className="cp-upload-icon" />
                          {isUploadingCover ? 'Uploading…' : 'Upload Custom Photo'}
                        </button>
                      </div>
                      {coverUploadError && <p className="cp-upload-error" style={{ marginBottom: 8 }}>{coverUploadError}</p>}

                      {/* Photo grid — custom photos first, then presets */}
                      <div className="cp-grid">
                        {/* Custom uploaded photos — each tile is independently selectable/deletable */}
                        {customCoverPhotos.map((photoUrl, idx) => {
                          // Strip cache-buster for comparison to determine active state
                          const activeBase = coverPhotoUrl?.split('?')[0]
                          const isActive = photoUrl === activeBase || photoUrl === coverPhotoUrl
                          const isDeleting = deletingCoverUrl === photoUrl
                          return (
                            <div key={photoUrl} className="cp-thumb-wrap">
                              <button
                                className={`cp-thumb ${isActive ? 'cp-thumb-active' : ''}`}
                                onClick={() => {
                                  if (isActive) return
                                  setCoverPhotoUrl(photoUrl)
                                  companyService.selectCoverPhoto(photoUrl)
                                }}
                              >
                                <div className="cp-thumb-img-wrap">
                                  <img src={photoUrl} alt={`Your Photo ${idx + 1}`} className="cp-thumb-img" />
                                  <div className="cp-thumb-overlay" style={{ backgroundColor: brandColor || '#3B82F6' }} />
                                </div>
                                <span className="cp-thumb-label">Your Photo {customCoverPhotos.length > 1 ? idx + 1 : ''}</span>
                                {isActive && (
                                  <span className="cp-thumb-check">
                                    <svg width="14" height="14" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                                  </span>
                                )}
                              </button>
                              {/* Delete badge */}
                              <button
                                className="cp-thumb-delete"
                                title="Remove this photo"
                                disabled={isDeleting || isUploadingCover}
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  if (!confirm('Remove this cover photo from your library?')) return
                                  setDeletingCoverUrl(photoUrl)
                                  try {
                                    const branding = await companyService.deleteOneCoverPhoto(photoUrl)
                                    try {
                                      const lib = typeof (branding as any).customCoverPhotos === 'string'
                                        ? JSON.parse((branding as any).customCoverPhotos)
                                        : ((branding as any).customCoverPhotos ?? [])
                                      if (Array.isArray(lib)) setCustomCoverPhotos(lib)
                                    } catch { /* keep existing */ }
                                    setCoverPhotoUrl(branding.coverPhotoUrl ?? '/images/covers/cover-1.jpg')
                                  } catch {
                                    // silent
                                  } finally {
                                    setDeletingCoverUrl(null)
                                  }
                                }}
                              >
                                {isDeleting
                                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/></svg>
                                  : <Trash2 size={12} />
                                }
                              </button>
                            </div>
                          )
                        })}

                        {/* Preset photos */}
                        {COVER_PHOTOS.map((photo) => {
                          const activeBase = coverPhotoUrl?.split('?')[0]
                          const isActive = photo.id === activeBase || photo.id === coverPhotoUrl
                          return (
                            <button
                              key={photo.id}
                              className={`cp-thumb ${isActive ? 'cp-thumb-active' : ''}`}
                              onClick={() => {
                                setCoverPhotoUrl(photo.id)
                                companyService.selectCoverPhoto(photo.id)
                              }}
                            >
                              <div className="cp-thumb-img-wrap">
                                <img src={photo.id} alt={photo.label} className="cp-thumb-img" style={{ objectPosition: photo.focus }} />
                                <div className="cp-thumb-overlay" style={{ backgroundColor: brandColor || '#3B82F6' }} />
                              </div>
                              <span className="cp-thumb-label">{photo.label}</span>
                              {isActive && (
                                <span className="cp-thumb-check">
                                  <svg width="14" height="14" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </>
                  ) : isCoverLetter ? (
                    <>
                      <div className="abt-content-header">
                        <h3 className="abt-content-title">Cover Letter</h3>
                        <label className="abt-toggle">
                          <input
                            type="checkbox"
                            checked={includeCoverLetter}
                            onChange={(e) => setIncludeCoverLetter(e.target.checked)}
                          />
                          <span>Include in all proposals</span>
                        </label>
                      </div>
                      <p className="abt-helper">Write a personalized cover letter that will appear at the beginning of your proposals.</p>
                      <textarea
                        key="coverLetter"
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        placeholder={activeItem.placeholder}
                        className="abt-textarea"
                      />
                      <div className="abt-estimator-preview">
                        <div className="pc-preview-label">Estimator Contact Preview</div>
                        <div className="pc-preview-name">Estimator Name</div>
                        <div className="pc-preview-detail">{companyName || 'Company Name'}</div>
                        <div className="pc-preview-detail">{[companyAddress, companyCity, companyState, companyZip].filter(Boolean).join(', ') || '123 Main Street, City, ST 00000'}</div>
                        <div className="pc-preview-detail">{companyPhone || '555-555-5555'}</div>
                        <div className="pc-preview-detail">{companyEmail || 'email@company.com'}</div>
                        <p className="pc-preview-note">The estimator's name and email are pulled from their user account. Company details come from the Contact Info tab.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="abt-content-header">
                        <h3 className="abt-content-title">{activeItem.label}</h3>
                        <label className="abt-toggle">
                          <input
                            type="checkbox"
                            checked={aboutSections[activeAboutKey]?.includeInProposal || false}
                            onChange={() => handleAboutSectionToggle(activeAboutKey)}
                          />
                          <span>Include in Proposal</span>
                        </label>
                      </div>
                      <textarea
                        key={activeAboutKey}
                        value={aboutSections[activeAboutKey]?.text || ''}
                        onChange={(e) => handleAboutSectionChange(activeAboutKey, e.target.value)}
                        placeholder={activeItem.placeholder}
                        className="abt-textarea"
                      />
                    </>
                  )}
                  {!isCoverPhoto && (
                    <div className="abt-footer">
                      <SaveStatus state={aboutSave} />
                      <Button onClick={handleSaveAbout} disabled={isSavingAbout}>
                        <Save className="mr-2 h-4 w-4" />
                        {isSavingAbout ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Proposal Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Choose a Template</h3>
                <p className="text-sm text-muted-foreground mb-6">Select the template style for your proposals</p>

                <div className="tpl-grid">
                  {TEMPLATES.map(template => (
                    <div
                      key={template.id}
                      className={`tpl-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                    >
                      {/* Thumbnail Preview */}
                      <div className="tpl-thumb">
                        {renderTemplateThumbnail(template.id)}
                      </div>

                      {/* Card Content */}
                      <div className="tpl-info">
                        <h4 className="tpl-name">{template.name}</h4>
                        <p className="tpl-desc">{template.desc}</p>
                      </div>

                      {/* Actions */}
                      <div className="tpl-actions">
                        <Button
                          variant="outline"
                          size="sm"
                          className="tpl-preview-btn"
                          onClick={() => navigate(`/proposals/preview?template=${template.id}&mode=settings-preview`)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Preview
                        </Button>
                        <button
                          className={`tpl-select-btn ${selectedTemplate === template.id ? 'selected' : ''}`}
                          onClick={async () => {
                            setSelectedTemplate(template.id)
                            try {
                              await companyService.updateSettings({ proposalTemplate: template.id })
                            } catch (err) {
                              console.error('Failed to save template selection:', err)
                            }
                          }}
                        >
                          {selectedTemplate === template.id ? 'Selected' : 'Select'}
                        </button>
                      </div>

                      {/* Active Badge */}
                      {selectedTemplate === template.id && (
                        <div className="tpl-selected-badge">
                          Active
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* Opportunity Feed Tab */}
          {activeTab === 'opportunity-feed' && (
            <OpportunityFeedSettings />
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <QuickBooksSettings />
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <BillingSettings />
          )}

          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Workflow Settings</h3>
                <label className="settings-toggle-row">
                  <div className="settings-toggle-text">
                    <span className="settings-toggle-label">Require Manager/Admin Approval</span>
                    <span className="settings-toggle-desc">When enabled, estimators must submit estimates for manager approval before creating a proposal. When disabled, estimators can create proposals directly.</span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={requireApproval}
                    className={`settings-toggle ${requireApproval ? 'active' : ''}`}
                    onClick={() => {
                      const newVal = !requireApproval
                      setRequireApproval(newVal)
                      companyService.updateSettings({ requireApproval: newVal }).catch(() => {})
                    }}
                  >
                    <span className="settings-toggle-thumb" />
                  </button>
                </label>
              </div>

              <div style={{ marginTop: '24px' }}>
                <h3 className="text-lg font-semibold mb-4">Feature Access</h3>
                <label className="settings-toggle-row">
                  <div className="settings-toggle-text">
                    <span className="settings-toggle-label">Enable Invoicing</span>
                    <span className="settings-toggle-desc">Allow your team to create, send, and track invoices for signed proposals.</span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={invoicingEnabled}
                    className={`settings-toggle ${invoicingEnabled ? 'active' : ''}`}
                    onClick={() => {
                      const newVal = !invoicingEnabled
                      setInvoicingEnabled(newVal)
                      companyService.updateSettings({ invoicingEnabled: newVal } as any).catch(() => {})
                      // Update auth store so sidebar reacts immediately
                      const stored = localStorage.getItem('paver_user')
                      if (stored) {
                        const u = JSON.parse(stored)
                        u.invoicingEnabled = newVal
                        localStorage.setItem('paver_user', JSON.stringify(u))
                        useAuthStore.getState().setUser(u)
                      }
                    }}
                  >
                    <span className="settings-toggle-thumb" />
                  </button>
                </label>

                {invoicingEnabled && (
                  <div className="settings-role-picker">
                    <span className="settings-toggle-label" style={{ fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                      Which roles can access Invoices?
                    </span>
                    {[
                      { value: 'ADMIN', label: 'Admin' },
                      { value: 'MANAGER', label: 'Manager' },
                      { value: 'ESTIMATOR', label: 'Estimator' },
                    ].map(role => (
                      <label key={role.value} className="settings-role-check">
                        <input
                          type="checkbox"
                          checked={invoicingRoles.includes(role.value)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...invoicingRoles, role.value]
                              : invoicingRoles.filter(r => r !== role.value)
                            setInvoicingRoles(updated)
                            companyService.updateSettings({ invoicingRoles: JSON.stringify(updated) } as any).catch(() => {})
                            // Update auth store so sidebar reacts immediately
                            const stored = localStorage.getItem('paver_user')
                            if (stored) {
                              const u = JSON.parse(stored)
                              u.invoicingRoles = updated
                              localStorage.setItem('paver_user', JSON.stringify(u))
                              useAuthStore.getState().setUser(u)
                            }
                          }}
                        />
                        <span>{role.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  )
}
